import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { authGuard } from 'ish-core/guards/auth.guard';
import { errorStatusGuard } from 'ish-core/guards/error-status.guard';
import { identityProviderInviteGuard } from 'ish-core/guards/identity-provider-invite.guard';
import { identityProviderLoginGuard } from 'ish-core/guards/identity-provider-login.guard';
import { identityProviderLogoutGuard } from 'ish-core/guards/identity-provider-logout.guard';
import { identityProviderRegisterGuard } from 'ish-core/guards/identity-provider-register.guard';
import { serverSettingGuard } from 'ish-core/guards/server-setting.guard';

const routes: Routes = [
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: 'loading', loadChildren: () => import('./loading/loading-page.module').then(m => m.LoadingPageModule) },
  /* DIAS PWA Customizations Start */
  /* CUSTOMIZATION: Presidio — restore stock home (DotCMS home deferred); login-gated storefront */
  {
    path: 'home',
    loadChildren: () => import('./home/home-page.module').then(m => m.HomePageModule),
    canActivate: [authGuard],
    data: {
      meta: {
        title: 'seo.title.home',
        description: 'seo.description.home',
      },
    },
  },
  /* DIAS PWA Customizations End */
  {
    path: 'error',
    loadChildren: () => import('./error/error-page.module').then(m => m.ErrorPageModule),
    data: {
      meta: {
        title: 'seo.title.error',
        robots: 'noindex, nofollow',
      },
    },
  },
  {
    path: 'maintenance',
    loadChildren: () => import('./maintenance/maintenance-page.module').then(m => m.MaintenancePageModule),
    canActivate: [errorStatusGuard],
    data: {
      meta: {
        title: 'seo.title.maintenance',
        robots: 'noindex, nofollow',
      },
    },
  },
  /* CUSTOMIZATION: public request-access MUST precede auth-gated /account */
  {
    path: 'account/request-access',
    loadChildren: () => import('./request-access/request-access-page.module').then(m => m.RequestAccessPageModule),
    data: {
      meta: {
        title: 'account.request_access.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
  /* CUSTOMIZATION: standalone account aliases — before /account parent */
  { path: 'account/approvals', redirectTo: 'account/requisitions/approver', pathMatch: 'full' },
  { path: 'account/saved-carts', redirectTo: 'account/order-templates', pathMatch: 'full' },
  { path: 'account/cost-centers', redirectTo: 'account/organization/cost-centers', pathMatch: 'full' },
  { path: 'account/users', redirectTo: 'account/organization/users', pathMatch: 'full' },
  { path: 'account/payment-methods', redirectTo: 'account/payment', pathMatch: 'full' },
  // CUSTOMIZATION: Quick Order is out of MVP Now — block deep links (extension kept dormant)
  { path: 'custom-quickorder', redirectTo: 'home', pathMatch: 'prefix' },
  { path: 'quickorder', redirectTo: 'home', pathMatch: 'prefix' },
  {
    path: 'account',
    loadChildren: () => import('./account/account-page.module').then(m => m.AccountPageModule),
    canActivate: [authGuard],
    data: {
      meta: {
        title: 'account.my_account.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
  {
    path: 'search',
    loadChildren: () => import('./search/search-page.module').then(m => m.SearchPageModule),
    canActivate: [authGuard],
  },
  {
    path: 'basket',
    loadChildren: () => import('./basket/basket-page.module').then(m => m.BasketPageModule),
    canActivate: [authGuard],
    data: {
      meta: {
        title: 'shopping_cart.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
  {
    path: 'checkout',
    loadChildren: () => import('./checkout/checkout-page.module').then(m => m.CheckoutPageModule),
    canActivate: [authGuard],
    data: {
      headerType: 'checkout',
      meta: {
        title: 'seo.title.checkout',
        robots: 'noindex, nofollow',
      },
    },
  },
  {
    path: 'register',
    loadChildren: () => import('./registration/registration-page.module').then(m => m.RegistrationPageModule),
    canActivate: [identityProviderRegisterGuard],
    data: {
      meta: {
        title: 'account.register.link',
        robots: 'noindex, nofollow',
      },
    },
  },
  {
    path: 'login',
    loadChildren: () => import('./login/login-page.module').then(m => m.LoginPageModule),
    canActivate: [identityProviderLoginGuard],
  },
  {
    path: 'logout',
    canActivate: [identityProviderLogoutGuard],
    children: [],
  },
  {
    path: 'invite',
    canActivate: [identityProviderInviteGuard],
    children: [],
  },
  {
    path: 'forgotPassword',
    loadChildren: () => import('./forgot-password/forgot-password-page.module').then(m => m.ForgotPasswordPageModule),
    data: {
      meta: {
        title: 'account.forgotdata.password_retrieval.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
  /* CUSTOMIZATION: standalone route aliases → DIAS destinations */
  { path: 'quickorder', redirectTo: 'quick-order', pathMatch: 'full' },
  { path: 'wishlist', redirectTo: 'account/order-templates', pathMatch: 'full' },
  { path: 'quote', redirectTo: 'account/quotes', pathMatch: 'full' },
  {
    // route for handling confirmation of user data and account deletion requests
    path: 'gdpr-requests',
    loadChildren: () => import('./data-request/data-request-page.module').then(m => m.DataRequestPageModule),
    data: {
      meta: {
        title: 'personal.data.request.title',
        robots: 'noindex, nofollow',
      },
    },
  },
  {
    path: 'withdrawal',
    loadChildren: () =>
      import('./withdrawal-request/withdrawal-request-page.module').then(m => m.WithdrawalRequestPageModule),
    canActivate: [serverSettingGuard],
    data: {
      serverSetting: 'preferences.WithdrawalPreferences.WithdrawalEnabled',
      meta: {
        title: 'account.withdrawal.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
  { path: 'cookies', loadChildren: () => import('./cookies/cookies-page.module').then(m => m.CookiesPageModule) },
  { path: 'cobrowse', loadChildren: () => import('./co-browse/co-browse-page.module').then(m => m.CoBrowsePageModule) },
  /* CUSTOMIZATION: Presidio — Contact your Presidio team resources (AM / ICM / collections / maintenance) */
  {
    path: 'contact-team',
    loadChildren: () => import('./contact-team/contact-team-page.module').then(m => m.ContactTeamPageModule),
    canActivate: [authGuard],
    data: {
      meta: {
        title: 'contact_team.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
  /* CUSTOMIZATION: Presidio — Help Center (FAQs + video tutorials) */
  {
    path: 'help-center',
    loadChildren: () => import('./help-center/help-center-page.module').then(m => m.HelpCenterPageModule),
    canActivate: [authGuard],
    data: {
      meta: {
        title: 'help_center.heading',
        robots: 'noindex, nofollow',
      },
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'enabled',
      initialNavigation: 'enabledBlocking',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
